import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Store, Bike, LogOut, RefreshCw, CheckCircle, XCircle, Clock, 
  Eye, Phone, Mail, MapPin, User, FileText, Calendar, Building,
  Shield, Loader2, Sparkles, ShoppingBag, TrendingUp, 
  DollarSign, Menu, Settings, Activity, Brain, BarChart3, Zap, MessageSquare,
  Lock, Unlock, Key, EyeOff, Trash2, RotateCcw, AlertTriangle, Pencil, Plus, Wallet, Camera, ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { DpEarningsDashboard } from './DpEarningsDashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// City list for AI Pricing
const CITIES = [
  'Jajpur', 'Cuttack', 'Bhubaneswar', 'Delhi', 'Mumbai', 'Bangalore', 
  'Chennai', 'Kolkata', 'Hyderabad', 'Pune', 'Ahmedabad'
];

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('orders');
  
  // Section access states
  const [sectionAccess, setSectionAccess] = useState({});
  const [sectionLoginModal, setSectionLoginModal] = useState(null);
  const [sectionUsername, setSectionUsername] = useState('');
  const [sectionPassword, setSectionPassword] = useState('');
  const [sectionLoginError, setSectionLoginError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Data states
  const [restaurantPartners, setRestaurantPartners] = useState([]);
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [customerInquiries, setCustomerInquiries] = useState([]);
  const [userLogins, setUserLogins] = useState([]);
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [priceRanges, setPriceRanges] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [complaintsStats, setComplaintsStats] = useState({ total: 0, by_status: {}, by_type: {} });
  
  // Complaint reply state
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyStatus, setReplyStatus] = useState('resolved');
  const [complaintsFilter, setComplaintsFilter] = useState('all');
  
  // Trash states
  const [trashItems, setTrashItems] = useState([]);
  const [selectedTrashBatch, setSelectedTrashBatch] = useState(null);
  const [trashBatchDetails, setTrashBatchDetails] = useState(null);
  
  // Menu filter states
  const [menuFilterRestaurant, setMenuFilterRestaurant] = useState('all');
  const [menuFilterLocation, setMenuFilterLocation] = useState('all');
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [editMenuForm, setEditMenuForm] = useState({ name: '', price: '', category: '', is_veg: true });
  const [parsingPdf, setParsingPdf] = useState(null);
  const [addMenuVendor, setAddMenuVendor] = useState(null);
  const [addMenuForm, setAddMenuForm] = useState({ name: '', price: '', category: '', is_veg: true, photoFile: null });
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  
  // AI Pricing states
  const [aiCity, setAiCity] = useState('Jajpur');
  const [aiDate, setAiDate] = useState(new Date().toISOString().split('T')[0]);
  const [aiLoading, setAiLoading] = useState(false);
  const [categorySummary, setCategorySummary] = useState([]);
  const [dishSummary, setDishSummary] = useState([]);

  // Withdrawal states
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalStats, setWithdrawalStats] = useState({});
  const [withdrawalFilter, setWithdrawalFilter] = useState('all');
  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ transaction_id: '', payment_method: 'Bank Transfer', remarks: '' });
  
  // Modal states
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [partnerType, setPartnerType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [verifyingDoc, setVerifyingDoc] = useState(null);
  const [verificationResults, setVerificationResults] = useState({});
  
  // Price range form
  const [priceForm, setPriceForm] = useState({
    pincode: '',
    dish: '',
    date: '',
    startPrice: '',
    peakPrice: '',
    lowPrice: '',
    averagePrice: '',
    peakTimeStart: '',
    peakTimeEnd: '',
    lowTimeStart: '',
    lowTimeEnd: '',
    avgTimeStart: '',
    avgTimeEnd: ''
  });

  useEffect(() => {
    const isAdmin = localStorage.getItem('fudco_admin');
    if (!isAdmin) {
      navigate('/admin');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [restaurantRes, deliveryRes, customerRes, userRes, orderRes, menuRes, priceRes, complaintsRes, complaintsStatsRes, trashRes, withdrawalRes, withdrawalStatsRes] = await Promise.all([
        axios.get(`${API}/admin/partners/restaurants`),
        axios.get(`${API}/admin/partners/delivery`),
        axios.get(`${API}/admin/customers`),
        axios.get(`${API}/admin/user-logins`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/orders`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/menu-items`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/price-ranges`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/complaints`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/complaints/stats`).catch(() => ({ data: { total: 0, by_status: {}, by_type: {} } })),
        axios.get(`${API}/admin/trash`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/withdrawals`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/withdrawals/stats`).catch(() => ({ data: {} }))
      ]);
      setRestaurantPartners(restaurantRes.data);
      setDeliveryPartners(deliveryRes.data);
      setCustomerInquiries(customerRes.data);
      setUserLogins(userRes.data);
      setOrders(orderRes.data);
      setMenuItems(menuRes.data);
      setPriceRanges(priceRes.data);
      setComplaints(complaintsRes.data);
      setComplaintsStats(complaintsStatsRes.data);
      setTrashItems(trashRes.data);
      setWithdrawals(withdrawalRes.data);
      setWithdrawalStats(withdrawalStatsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('fudco_admin');
    localStorage.removeItem('fudco_section_access');
    navigate('/admin');
  };

  // Section Access Functions - REMOVED LOCK SYSTEM
  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
  };

  const handleSectionLogin = async () => {
    try {
      const response = await axios.post(`${API}/admin/section/login`, {
        section_id: sectionLoginModal,
        username: sectionUsername,
        password: sectionPassword
      });
      
      if (response.data.success) {
        const newAccess = { ...sectionAccess, [sectionLoginModal]: true };
        setSectionAccess(newAccess);
        localStorage.setItem('fudco_section_access', JSON.stringify(newAccess));
        setActiveSection(sectionLoginModal);
        setSectionLoginModal(null);
        toast.success(response.data.message);
      }
    } catch (error) {
      setSectionLoginError(error.response?.data?.detail || 'Invalid credentials');
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/section/change-password`, {
        section_id: sectionLoginModal,
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  // Load saved section access on mount
  useEffect(() => {
    const savedAccess = localStorage.getItem('fudco_section_access');
    if (savedAccess) {
      try {
        setSectionAccess(JSON.parse(savedAccess));
      } catch (e) {
        localStorage.removeItem('fudco_section_access');
      }
    }
  }, []);

  const handleViewDetails = (partner, type) => {
    setSelectedPartner(partner);
    setPartnerType(type);
    setVerificationResults({});
    setShowDetailModal(true);
  };

  const handleUpdateStatus = async (partnerId, status, type) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/partners/${type}/status`, {
        partner_id: partnerId,
        status: status
      });
      toast.success(`Partner ${status === 'approved' ? 'approved' : 'rejected'} successfully!`);
      setShowDetailModal(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMenuAction = async (itemId, action) => {
    try {
      await axios.post(`${API}/admin/menu-items/${itemId}/status`, { status: action });
      toast.success(`Menu item ${action}!`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update menu item');
    }
  };

  const handleEditMenuItem = async () => {
    if (!editingMenuItem) return;
    try {
      await axios.put(`${API}/admin/menu-items/${editingMenuItem.id}`, {
        name: editMenuForm.name,
        price: parseFloat(editMenuForm.price),
        category: editMenuForm.category,
        is_veg: editMenuForm.is_veg
      });
      toast.success('Menu item updated!');
      setEditingMenuItem(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update menu item');
    }
  };

  const handlePhotoUpload = async (itemId, file) => {
    if (!file) return;
    setUploadingPhoto(itemId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/menu-items/${itemId}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo updated!');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const getPhotoUrl = (item) => {
    if (!item.photo_url) return null;
    const url = item.photo_url;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const handleParsePdf = async (partnerId, restaurantName) => {
    setParsingPdf(partnerId);
    try {
      const response = await axios.post(`${API}/admin/parse-menu-pdf/${partnerId}`);
      toast.success(`${response.data.items_count} items extracted from ${restaurantName}'s menu PDF`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse menu PDF');
    } finally {
      setParsingPdf(null);
    }
  };

  const handleAddMenuItem = async () => {
    if (!addMenuVendor || !addMenuForm.name || !addMenuForm.price || !addMenuForm.category) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/menu-items`, {
        partner_id: addMenuVendor.id,
        name: addMenuForm.name,
        price: parseFloat(addMenuForm.price),
        category: addMenuForm.category,
        is_veg: addMenuForm.is_veg
      });
      const newItemId = res.data.item?.id;
      // Upload photo if selected
      if (addMenuForm.photoFile && newItemId) {
        const formData = new FormData();
        formData.append('file', addMenuForm.photoFile);
        await axios.post(`${API}/admin/menu-items/${newItemId}/photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`Menu item added for ${addMenuVendor.restaurant_name}`);
      setAddMenuVendor(null);
      setAddMenuForm({ name: '', price: '', category: '', is_veg: true, photoFile: null });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add menu item');
    }
  };

  const handleWithdrawalAction = async (id, action, remarks = '') => {
    try {
      await axios.put(`${API}/admin/withdrawals/${id}/${action}`, { remarks });
      toast.success(`Withdrawal ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} withdrawal`);
    }
  };

  const handlePaymentDone = async () => {
    if (!paymentModal) return;
    try {
      await axios.put(`${API}/admin/withdrawals/${paymentModal.id}/payment-done`, paymentForm);
      toast.success('Payment marked as done!');
      setPaymentModal(null);
      setPaymentForm({ transaction_id: '', payment_method: 'Bank Transfer', remarks: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    }
  };

  const sendWhatsApp = (phone, withdrawal) => {
    const pd = withdrawal.payment_details || {};
    const msg = `*Fudco Payment Confirmation*%0a%0aHi ${withdrawal.requester_name},%0a%0aYour withdrawal of *Rs.${withdrawal.amount}* has been processed.%0a%0aTransaction ID: ${pd.transaction_id || 'N/A'}%0aPayment Method: ${pd.payment_method || 'N/A'}%0aPaid On: ${pd.paid_at ? new Date(pd.paid_at).toLocaleDateString('en-IN') : 'N/A'}%0a${pd.remarks ? 'Remarks: ' + pd.remarks : ''}%0a%0aThank you,%0aFudco Team`;
    const cleanPhone = phone.replace(/\D/g, '');
    const waPhone = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`;
    window.open(`https://wa.me/${waPhone}?text=${msg}`, '_blank');
  };

  const runAIAnalysis = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/admin/ai-pricing-analysis`, {
        city: aiCity,
        date: aiDate
      });
      setCategorySummary(response.data.category_summary || []);
      setDishSummary(response.data.dish_summary || []);
      toast.success('AI Analysis completed!');
    } catch (error) {
      toast.error('Failed to run AI analysis');
      // Set demo data if API fails
      setCategorySummary([
        { category: 'Lunch', avgPrice: 180, indexValue: 102.5, totalDishes: 45 },
        { category: 'Dinner', avgPrice: 220, indexValue: 98.3, totalDishes: 52 },
        { category: 'Breakfast', avgPrice: 120, indexValue: 105.2, totalDishes: 28 },
        { category: 'Snacks', avgPrice: 80, indexValue: 101.8, totalDishes: 35 },
      ]);
      setDishSummary([
        { dish: 'Chicken Biryani', category: 'Lunch', avgPrice: 220, start: 200, high: 280, low: 180, status: 'rising' },
        { dish: 'Paneer Butter Masala', category: 'Dinner', avgPrice: 180, start: 170, high: 220, low: 160, status: 'stable' },
        { dish: 'Masala Dosa', category: 'Breakfast', avgPrice: 80, start: 75, high: 95, low: 70, status: 'falling' },
        { dish: 'Fish Curry', category: 'Lunch', avgPrice: 250, start: 230, high: 300, low: 210, status: 'rising' },
        { dish: 'Veg Thali', category: 'Lunch', avgPrice: 150, start: 140, high: 180, low: 130, status: 'stable' },
      ]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSetPrice = async (dish) => {
    setPriceForm({
      ...priceForm,
      dish: dish.dish,
      date: aiDate,
      startPrice: dish.start?.toString() || '',
      peakPrice: dish.high?.toString() || '',
      lowPrice: dish.low?.toString() || '',
      averagePrice: dish.avgPrice?.toString() || ''
    });
    setActiveSection('pricing');
    toast.info(`Price form pre-filled for ${dish.dish}`);
  };

  const [seedingData, setSeedingData] = useState(false);

  const handleSeedMockData = async () => {
    setSeedingData(true);
    try {
      // Seed restaurants
      const restRes = await axios.post(`${API}/admin/seed-mock-restaurants`);
      toast.success(restRes.data.message);
      
      // Seed orders
      const orderRes = await axios.post(`${API}/admin/seed-mock-orders`);
      toast.success(orderRes.data.message);
      
      // Refresh data
      await fetchAllData();
    } catch (error) {
      toast.error('Failed to seed mock data');
    } finally {
      setSeedingData(false);
    }
  };

  const handleSavePrice = async () => {
    if (!priceForm.pincode || !priceForm.dish || !priceForm.date) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API}/admin/price-ranges`, priceForm);
      toast.success('Price range saved successfully!');
      setPriceForm({ 
        pincode: '', 
        dish: '', 
        date: '', 
        startPrice: '', 
        peakPrice: '', 
        lowPrice: '',
        averagePrice: '',
        peakTimeStart: '',
        peakTimeEnd: '',
        lowTimeStart: '',
        lowTimeEnd: '',
        avgTimeStart: '',
        avgTimeEnd: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save price range');
    }
  };

  const handleVerifyDocument = async (documentUrl, documentType) => {
    if (!documentUrl) {
      toast.error('No document URL available');
      return;
    }

    setVerifyingDoc(documentType);
    try {
      const formData = new FormData();
      formData.append('document_url', documentUrl);
      formData.append('document_type', documentType);

      const response = await axios.post(`${API}/admin/verify-document-url`, formData);
      
      const result = response.data.verification_result;
      setVerificationResults(prev => ({
        ...prev,
        [documentType]: result
      }));

      if (result.verified) {
        toast.success(`Document verified successfully!`, {
          description: `Confidence: ${result.confidence}%`
        });
      } else {
        toast.warning(`Document verification failed`, {
          description: result.error || 'Could not verify document'
        });
      }
    } catch (error) {
      toast.error('Failed to verify document');
    } finally {
      setVerifyingDoc(null);
    }
  };

  const handleDeleteItem = async (endpoint, id, label) => {
    if (!window.confirm(`Are you sure you want to delete this ${label}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/${endpoint}/${id}`);
      toast.success(`${label} deleted successfully`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${label}`);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      under_review: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-700',
      online: 'bg-green-100 text-green-700',
      offline: 'bg-red-100 text-red-700',
      delivered: 'bg-green-100 text-green-700',
      preparing: 'bg-blue-100 text-blue-700',
      in_transit: 'bg-purple-100 text-purple-700',
    };
    return (
      <Badge className={`${styles[status] || styles.pending} font-medium`}>
        {status?.replace('_', ' ').toUpperCase() || 'PENDING'}
      </Badge>
    );
  };

  const stats = {
    totalRestaurants: restaurantPartners.length,
    pendingRestaurants: restaurantPartners.filter(p => p.status === 'pending' || p.status === 'under_review').length,
    totalDelivery: deliveryPartners.length,
    pendingDelivery: deliveryPartners.filter(p => p.status === 'pending' || p.status === 'under_review').length,
    totalCustomers: customerInquiries.length,
    totalOrders: orders.length,
    totalUsers: userLogins.length,
  };

  const sidebarItems = [
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'vendors', label: 'Vendors', icon: Store },
    { id: 'delivery', label: 'Delivery Partners', icon: Bike },
    { id: 'dp-earnings', label: 'DP Earnings', icon: TrendingUp },
    { id: 'menu', label: 'Menu Catalog', icon: Menu },
    { id: 'pricing', label: 'Price Range Setup', icon: DollarSign },
    { id: 'ai-pricing', label: 'AI Pricing Intelligence', icon: Brain },
    { id: 'complaints', label: 'Complaints & Feedback', icon: Mail },
    { id: 'customers', label: 'Customer Inquiries', icon: User },
    { id: 'trash', label: 'Trash (7 days)', icon: Trash2 },
    { id: 'withdrawals', label: 'Withdrawals', icon: Wallet },
  ];

  const renderDocVerify = (label, url, docType) => {
    const result = verificationResults[docType];
    const isVerifying = verifyingDoc === docType;

    return (
      <div className="border rounded-lg p-3 bg-gray-50" key={docType}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {label}
          </span>
          {result && (
            <Badge className={result.verified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
              {result.verified ? `Verified (${result.confidence}%)` : 'Failed'}
            </Badge>
          )}
        </div>
        
        {url ? (
          <div className="space-y-2">
            <a 
              href={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline block"
            >
              View Document
            </a>
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={() => handleVerifyDocument(url, docType)}
              disabled={isVerifying}
            >
              {isVerifying ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Verifying...</>
              ) : (
                <><Sparkles className="w-3 h-3 mr-1" /> AI Verify</>
              )}
            </Button>
            
            {result && result.details && (
              <div className="mt-2 p-2 bg-white rounded border text-xs">
                <p className="font-semibold mb-1">Extracted Data:</p>
                {result.details.name && <p>Name: {result.details.name}</p>}
                {result.details.aadhar_number && <p>Aadhar: {result.details.aadhar_number}</p>}
                {result.details.pan_number && <p>PAN: {result.details.pan_number}</p>}
                {result.details.license_number && <p>License: {result.details.license_number}</p>}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400">Not uploaded</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-[#0a7d4f]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#0a7d4f] text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <h1 className="text-xl font-bold">FUDCO ADMIN DASHBOARD</h1>
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleSeedMockData} 
            disabled={seedingData}
            className="text-white hover:bg-white/20 border border-white/30"
            data-testid="seed-mock-data-btn"
          >
            {seedingData ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Seeding...</>
            ) : (
              <><Zap className="w-4 h-4 mr-2" /> Seed Mock Data</>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchAllData} className="text-white hover:bg-white/20">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/20">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 bg-gray-800 min-h-[calc(100vh-64px)]">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleSectionClick(item.id)}
              className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                activeSection === item.id 
                  ? 'bg-[#0a7d4f] text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
          
          {/* Clear All Data Button */}
          <div className="p-4 mt-4 border-t border-gray-700">
            <Button 
              variant="destructive" 
              size="sm" 
              className="w-full bg-red-600 hover:bg-red-700 text-white text-xs"
              onClick={async () => {
                if (window.confirm('⚠️ WARNING: This will DELETE ALL DATA including orders, vendors, delivery partners, menu items, complaints, etc.\n\nAre you sure you want to clear ALL data?')) {
                  if (window.confirm('🚨 FINAL CONFIRMATION: This action cannot be undone!\n\nType "yes" to confirm.')) {
                    try {
                      const response = await axios.delete(`${API}/admin/clear-all-data`);
                      toast.success('All data cleared successfully!');
                      fetchAllData();
                    } catch (error) {
                      toast.error('Failed to clear data');
                    }
                  }
                }
              }}
            >
              🗑️ Clear All Data
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ShoppingBag className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalOrders}</p>
                  <p className="text-xs text-gray-500">Orders</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Store className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalRestaurants}</p>
                  <p className="text-xs text-gray-500">Vendors</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Bike className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalDelivery}</p>
                  <p className="text-xs text-gray-500">Riders</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Menu className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{menuItems.length}</p>
                  <p className="text-xs text-gray-500">Menu Items</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pendingRestaurants}</p>
                  <p className="text-xs text-gray-500">Pending Vendors</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <User className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalCustomers}</p>
                  <p className="text-xs text-gray-500">Inquiries</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Order Records */}
          {activeSection === 'orders' && (
            <Card className="p-6 bg-white" data-testid="orders-section">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#0a7d4f]" />
                Order Records
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Order ID</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">User</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Restaurant</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Rider</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length > 0 ? orders.map((order, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono">{order.id?.slice(0, 8)}...</td>
                        <td className="px-4 py-3 text-sm">{order.user_name || '-'}</td>
                        <td className="px-4 py-3 text-sm">{order.restaurant_name || '-'}</td>
                        <td className="px-4 py-3 text-sm">{order.rider_name || '-'}</td>
                        <td className="px-4 py-3">{getStatusBadge(order.status)}</td>
                        <td className="px-4 py-3 text-sm font-semibold">₹{order.amount || 0}</td>
                        <td className="px-4 py-3">
                          <Button data-testid={`delete-order-${idx}`} size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteItem('admin/orders', order.id, 'Order')}>
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Vendor Active Status */}
          {activeSection === 'vendors' && (
            <Card className="p-6 bg-white" data-testid="vendors-section">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Store className="w-5 h-5 text-[#0a7d4f]" />
                Vendor Active Status
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Restaurant</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Owner</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Phone</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Menu PDF</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Last Active</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {restaurantPartners.length > 0 ? restaurantPartners.map((vendor, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium">{vendor.restaurant_name}</td>
                        <td className="px-4 py-3 text-sm">{vendor.owner_name}</td>
                        <td className="px-4 py-3 text-sm">{vendor.phone}</td>
                        <td className="px-4 py-3">{getStatusBadge(vendor.status)}</td>
                        <td className="px-4 py-3 text-sm">
                          {(vendor.menu_pdf_url || vendor.menu_pdf) ? (
                            <a
                              href={(() => {
                                const pdf = vendor.menu_pdf_url || vendor.menu_pdf;
                                if (pdf.startsWith('http')) return pdf;
                                if (pdf.startsWith('/api/files/')) return `${process.env.REACT_APP_BACKEND_URL}${pdf}`;
                                if (pdf.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${pdf}`;
                                return `${process.env.REACT_APP_BACKEND_URL}/api/files/menus/${pdf}`;
                              })()}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                              data-testid={`view-pdf-${idx}`}
                            >
                              <FileText className="w-4 h-4" />
                              <span>View PDF</span>
                            </a>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-500">Not Uploaded</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">{vendor.last_active || vendor.created_at?.split('T')[0]}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => handleViewDetails(vendor, 'restaurant')}>
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                            <Button
                              data-testid={`add-menu-${idx}`}
                              size="sm"
                              className="bg-[#0a7d4f] hover:bg-[#086b43] text-white"
                              onClick={() => {
                                setAddMenuVendor(vendor);
                                setAddMenuForm({ name: '', price: '', category: '', is_veg: true });
                              }}
                            >
                              <Plus className="w-4 h-4 mr-1" /> Add Menu
                            </Button>
                            {(vendor.menu_pdf_url || vendor.menu_pdf) && (
                              <Button
                                data-testid={`extract-menu-${idx}`}
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                disabled={parsingPdf === vendor.id}
                                onClick={() => handleParsePdf(vendor.id, vendor.restaurant_name)}
                              >
                                {parsingPdf === vendor.id ? (
                                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Extracting...</>
                                ) : (
                                  <><Sparkles className="w-4 h-4 mr-1" /> Extract Menu</>
                                )}
                              </Button>
                            )}
                            <Button data-testid={`delete-vendor-${idx}`} size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteItem('admin/restaurants', vendor.id, 'Restaurant')}>
                              <Trash2 className="w-4 h-4 mr-1" /> Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No vendors found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Add Menu Item Modal */}
          {addMenuVendor && (
            <Dialog open={!!addMenuVendor} onOpenChange={() => setAddMenuVendor(null)}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-[#0a7d4f]" />
                    Add Menu Item — {addMenuVendor.restaurant_name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Dish Photo (optional)</Label>
                    <div className="mt-1 flex items-center gap-4">
                      {addMenuForm.photoFile ? (
                        <img src={URL.createObjectURL(addMenuForm.photoFile)} alt="" className="w-20 h-20 rounded-lg object-cover border" />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <label data-testid="add-menu-photo">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => setAddMenuForm({...addMenuForm, photoFile: e.target.files[0] || null})} />
                        <Button size="sm" variant="outline" asChild>
                          <span><Camera className="w-4 h-4 mr-1" /> {addMenuForm.photoFile ? 'Change' : 'Add Photo'}</span>
                        </Button>
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label>Dish Name</Label>
                    <Input data-testid="add-menu-name" placeholder="e.g. Paneer Tikka" value={addMenuForm.name} onChange={(e) => setAddMenuForm({...addMenuForm, name: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Price (₹)</Label>
                    <Input data-testid="add-menu-price" type="number" placeholder="e.g. 250" value={addMenuForm.price} onChange={(e) => setAddMenuForm({...addMenuForm, price: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Input data-testid="add-menu-category" placeholder="e.g. Starters, Main Course, Beverages" value={addMenuForm.category} onChange={(e) => setAddMenuForm({...addMenuForm, category: e.target.value})} className="mt-1" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Label>Type:</Label>
                    <Button
                      data-testid="add-menu-veg"
                      size="sm"
                      variant={addMenuForm.is_veg ? 'default' : 'outline'}
                      className={addMenuForm.is_veg ? 'bg-green-600 hover:bg-green-700' : ''}
                      onClick={() => setAddMenuForm({...addMenuForm, is_veg: true})}
                    >Veg</Button>
                    <Button
                      data-testid="add-menu-nonveg"
                      size="sm"
                      variant={!addMenuForm.is_veg ? 'default' : 'outline'}
                      className={!addMenuForm.is_veg ? 'bg-red-600 hover:bg-red-700' : ''}
                      onClick={() => setAddMenuForm({...addMenuForm, is_veg: false})}
                    >Non-Veg</Button>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button data-testid="add-menu-submit" className="flex-1 bg-[#0a7d4f] hover:bg-[#086b43]" onClick={handleAddMenuItem}>
                      Add to Menu Catalog
                    </Button>
                    <Button variant="outline" onClick={() => setAddMenuVendor(null)}>Cancel</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Delivery Partner Activity */}
          {activeSection === 'delivery' && (
            <Card className="p-6 bg-white" data-testid="delivery-section">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Bike className="w-5 h-5 text-[#0a7d4f]" />
                Delivery Partner Activity
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Rider</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Phone</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">KYC</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Current Order</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Location</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveryPartners.length > 0 ? deliveryPartners.map((rider, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium">{rider.name}</td>
                        <td className="px-4 py-3 text-sm">{rider.phone}</td>
                        <td className="px-4 py-3">{getStatusBadge(rider.status)}</td>
                        <td className="px-4 py-3">
                          <Badge className={rider.kyc_submitted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {rider.kyc_submitted ? 'Submitted' : 'Pending'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm">{rider.current_order || '-'}</td>
                        <td className="px-4 py-3 text-sm">{rider.city || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleViewDetails(rider, 'delivery')}>
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                            <Button data-testid={`delete-delivery-${idx}`} size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteItem('admin/delivery-partners', rider.id, 'Delivery Partner')}>
                              <Trash2 className="w-4 h-4 mr-1" /> Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No delivery partners found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* DP Earnings Dashboard */}
          {activeSection === 'dp-earnings' && (
            <DpEarningsDashboard />
          )}

          {/* Menu Catalog */}
          {activeSection === 'menu' && (
            <Card className="p-6 bg-white" data-testid="menu-section">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Menu className="w-5 h-5 text-[#0a7d4f]" />
                Menu Catalog
              </h3>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label htmlFor="filterRestaurant" className="text-sm font-medium">Filter by Restaurant</Label>
                  <Select value={menuFilterRestaurant} onValueChange={setMenuFilterRestaurant}>
                    <SelectTrigger id="filterRestaurant" className="mt-1">
                      <SelectValue placeholder="All Restaurants" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Restaurants</SelectItem>
                      {restaurantPartners.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.restaurant_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="filterLocation" className="text-sm font-medium">Filter by Location/City</Label>
                  <Select value={menuFilterLocation} onValueChange={setMenuFilterLocation}>
                    <SelectTrigger id="filterLocation" className="mt-1">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {[...new Set([
                        ...restaurantPartners.map(r => r.city),
                        ...menuItems.map(m => m.restaurant_city)
                      ].filter(Boolean))].map((city) => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    onClick={() => { setMenuFilterRestaurant('all'); setMenuFilterLocation('all'); }}
                    className="w-full"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Clear Filters
                  </Button>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Photo</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Restaurant</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Dish Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Category</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Price</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {menuItems.length > 0 ? menuItems
                      .filter(item => {
                        if (menuFilterRestaurant !== 'all' && item.partner_id !== menuFilterRestaurant) return false;
                        if (menuFilterLocation !== 'all') {
                          if ((item.restaurant_city || '') !== menuFilterLocation) return false;
                        }
                        return true;
                      })
                      .map((item, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3">
                              {getPhotoUrl(item) ? (
                                <div className="relative group cursor-pointer" onClick={() => setViewingPhoto(item)}>
                                  <img src={getPhotoUrl(item)} alt={item.name} className="w-14 h-14 rounded-lg object-cover border border-gray-200 group-hover:border-[#0a7d4f] transition-all" />
                                  <div className="absolute inset-0 bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                    <Eye className="w-4 h-4 text-white" />
                                  </div>
                                </div>
                              ) : (
                                <label className="w-14 h-14 rounded-lg border-2 border-dashed border-gray-300 hover:border-[#0a7d4f] flex items-center justify-center cursor-pointer transition-colors" data-testid={`add-photo-${idx}`}>
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(item.id, e.target.files[0])} />
                                  {uploadingPhoto === item.id ? (
                                    <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                                  ) : (
                                    <Camera className="w-5 h-5 text-gray-400" />
                                  )}
                                </label>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium">{item.restaurant_name || '-'}</td>
                            <td className="px-4 py-3 text-sm font-medium">{item.name}</td>
                            <td className="px-4 py-3 text-sm">{item.category}</td>
                            <td className="px-4 py-3 text-sm font-semibold">Rs.{item.price}</td>
                            <td className="px-4 py-3">
                              <Badge className={item.is_veg ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                {item.is_veg ? 'Veg' : 'Non-Veg'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                {getPhotoUrl(item) && (
                                  <label data-testid={`change-photo-${idx}`}>
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(item.id, e.target.files[0])} />
                                    <Button size="sm" variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50 cursor-pointer" asChild>
                                      <span><Camera className="w-4 h-4" /></span>
                                    </Button>
                                  </label>
                                )}
                                <Button
                                  data-testid={`edit-menu-${idx}`}
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                  onClick={() => {
                                    setEditingMenuItem(item);
                                    setEditMenuForm({ name: item.name, price: item.price.toString(), category: item.category, is_veg: item.is_veg });
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button data-testid={`delete-menu-${idx}`} size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteItem('admin/menu-items', item.id, 'Menu Item')}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                      )) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No menu items found. Import from vendor PDFs above or add items manually.
                        </td>
                      </tr>
                    )}
                    {menuItems.length > 0 && menuItems.filter(item => {
                      if (menuFilterRestaurant !== 'all' && item.partner_id !== menuFilterRestaurant) return false;
                      if (menuFilterLocation !== 'all' && item.restaurant_city !== menuFilterLocation) return false;
                      return true;
                    }).length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No menu items found for selected filters
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* View Photo Modal */}
              {viewingPhoto && (
                <Dialog open={!!viewingPhoto} onOpenChange={() => setViewingPhoto(null)}>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <ImageIcon className="w-5 h-5 text-[#0a7d4f]" />
                        {viewingPhoto.name}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <img src={getPhotoUrl(viewingPhoto)} alt={viewingPhoto.name} className="w-full rounded-lg object-contain max-h-[400px] border" />
                      <div className="flex gap-2">
                        <label className="flex-1" data-testid="change-photo-modal">
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => { handlePhotoUpload(viewingPhoto.id, e.target.files[0]); setViewingPhoto(null); }} />
                          <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" asChild>
                            <span><Camera className="w-4 h-4 mr-2" /> Change Photo</span>
                          </Button>
                        </label>
                        <Button variant="outline" onClick={() => setViewingPhoto(null)}>Close</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {/* Edit Menu Item Modal */}
              {editingMenuItem && (
                <Dialog open={!!editingMenuItem} onOpenChange={() => setEditingMenuItem(null)}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Pencil className="w-5 h-5 text-[#0a7d4f]" />
                        Edit Menu Item
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Photo Section */}
                      <div>
                        <Label>Dish Photo</Label>
                        <div className="mt-1 flex items-center gap-4">
                          {getPhotoUrl(editingMenuItem) ? (
                            <img src={getPhotoUrl(editingMenuItem)} alt="" className="w-20 h-20 rounded-lg object-cover border" />
                          ) : (
                            <div className="w-20 h-20 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                              <ImageIcon className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <label data-testid="edit-menu-photo-upload">
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(editingMenuItem.id, e.target.files[0])} />
                            <Button size="sm" variant="outline" asChild>
                              <span><Camera className="w-4 h-4 mr-1" /> {getPhotoUrl(editingMenuItem) ? 'Change' : 'Add Photo'}</span>
                            </Button>
                          </label>
                        </div>
                      </div>
                      <div>
                        <Label>Dish Name</Label>
                        <Input data-testid="edit-menu-name" value={editMenuForm.name} onChange={(e) => setEditMenuForm({...editMenuForm, name: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label>Price (Rs.)</Label>
                        <Input data-testid="edit-menu-price" type="number" value={editMenuForm.price} onChange={(e) => setEditMenuForm({...editMenuForm, price: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Input data-testid="edit-menu-category" value={editMenuForm.category} onChange={(e) => setEditMenuForm({...editMenuForm, category: e.target.value})} className="mt-1" />
                      </div>
                      <div className="flex items-center gap-3">
                        <Label>Type:</Label>
                        <Button
                          data-testid="edit-menu-veg-toggle"
                          size="sm"
                          variant={editMenuForm.is_veg ? 'default' : 'outline'}
                          className={editMenuForm.is_veg ? 'bg-green-600 hover:bg-green-700' : ''}
                          onClick={() => setEditMenuForm({...editMenuForm, is_veg: true})}
                        >Veg</Button>
                        <Button
                          size="sm"
                          variant={!editMenuForm.is_veg ? 'default' : 'outline'}
                          className={!editMenuForm.is_veg ? 'bg-red-600 hover:bg-red-700' : ''}
                          onClick={() => setEditMenuForm({...editMenuForm, is_veg: false})}
                        >Non-Veg</Button>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button data-testid="edit-menu-save" className="flex-1 bg-[#0a7d4f] hover:bg-[#086b43]" onClick={handleEditMenuItem}>
                          Save Changes
                        </Button>
                        <Button variant="outline" onClick={() => setEditingMenuItem(null)}>Cancel</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </Card>
          )}

          {/* Price Range Setup */}
          {activeSection === 'pricing' && (
            <Card className="p-6 bg-white" data-testid="pricing-section">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#0a7d4f]" />
                Set Dish Price Range (Pincode + Date + Time)
              </h3>
              
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Form */}
                <div className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pincode">Pincode *</Label>
                      <Input 
                        id="pincode" 
                        value={priceForm.pincode}
                        onChange={(e) => setPriceForm({...priceForm, pincode: e.target.value})}
                        placeholder="Enter pincode"
                      />
                    </div>
                    <div>
                      <Label htmlFor="date">Date *</Label>
                      <Input 
                        id="date" 
                        type="date"
                        value={priceForm.date}
                        onChange={(e) => setPriceForm({...priceForm, date: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="dish">Dish Name *</Label>
                    <Input 
                      id="dish" 
                      value={priceForm.dish}
                      onChange={(e) => setPriceForm({...priceForm, dish: e.target.value})}
                      placeholder="Enter dish name"
                    />
                  </div>

                  {/* Price Fields */}
                  <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                    <h4 className="font-semibold text-sm text-gray-700">Price Configuration</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="startPrice">Start/Opening Price (₹)</Label>
                        <Input 
                          id="startPrice" 
                          type="number"
                          value={priceForm.startPrice}
                          onChange={(e) => setPriceForm({...priceForm, startPrice: e.target.value})}
                          placeholder="Opening price"
                        />
                      </div>
                      <div>
                        <Label htmlFor="averagePrice">Average Price (₹)</Label>
                        <Input 
                          id="averagePrice" 
                          type="number"
                          value={priceForm.averagePrice}
                          onChange={(e) => setPriceForm({...priceForm, averagePrice: e.target.value})}
                          placeholder="Average price"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="peakPrice">Peak Price (₹)</Label>
                        <Input 
                          id="peakPrice" 
                          type="number"
                          value={priceForm.peakPrice}
                          onChange={(e) => setPriceForm({...priceForm, peakPrice: e.target.value})}
                          placeholder="Maximum price"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lowPrice">Lowest Price (₹)</Label>
                        <Input 
                          id="lowPrice" 
                          type="number"
                          value={priceForm.lowPrice}
                          onChange={(e) => setPriceForm({...priceForm, lowPrice: e.target.value})}
                          placeholder="Minimum price"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Time Range Fields */}
                  <div className="p-4 bg-blue-50 rounded-lg space-y-4">
                    <h4 className="font-semibold text-sm text-blue-700 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Time Range Configuration
                    </h4>
                    
                    {/* Peak Time */}
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <Label className="text-red-600 font-medium mb-2 block">Peak Price Time Range</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="peakTimeStart" className="text-xs text-gray-500">From</Label>
                          <Input 
                            id="peakTimeStart" 
                            type="time"
                            value={priceForm.peakTimeStart}
                            onChange={(e) => setPriceForm({...priceForm, peakTimeStart: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="peakTimeEnd" className="text-xs text-gray-500">To</Label>
                          <Input 
                            id="peakTimeEnd" 
                            type="time"
                            value={priceForm.peakTimeEnd}
                            onChange={(e) => setPriceForm({...priceForm, peakTimeEnd: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Low Time */}
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <Label className="text-green-600 font-medium mb-2 block">Lowest Price Time Range</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="lowTimeStart" className="text-xs text-gray-500">From</Label>
                          <Input 
                            id="lowTimeStart" 
                            type="time"
                            value={priceForm.lowTimeStart}
                            onChange={(e) => setPriceForm({...priceForm, lowTimeStart: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="lowTimeEnd" className="text-xs text-gray-500">To</Label>
                          <Input 
                            id="lowTimeEnd" 
                            type="time"
                            value={priceForm.lowTimeEnd}
                            onChange={(e) => setPriceForm({...priceForm, lowTimeEnd: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Average Time */}
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <Label className="text-blue-600 font-medium mb-2 block">Average Price Time Range</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="avgTimeStart" className="text-xs text-gray-500">From</Label>
                          <Input 
                            id="avgTimeStart" 
                            type="time"
                            value={priceForm.avgTimeStart}
                            onChange={(e) => setPriceForm({...priceForm, avgTimeStart: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="avgTimeEnd" className="text-xs text-gray-500">To</Label>
                          <Input 
                            id="avgTimeEnd" 
                            type="time"
                            value={priceForm.avgTimeEnd}
                            onChange={(e) => setPriceForm({...priceForm, avgTimeEnd: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button 
                    onClick={handleSavePrice}
                    className="w-full bg-[#0a7d4f] hover:bg-[#086b42] text-white h-12"
                  >
                    Save Price Range
                  </Button>
                </div>

                {/* Existing Price Ranges */}
                <div>
                  <h4 className="font-semibold mb-3">Existing Price Ranges</h4>
                  <div className="max-h-[600px] overflow-y-auto space-y-3">
                    {priceRanges.length > 0 ? priceRanges.map((range, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg border">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-lg">{range.dish}</p>
                            <p className="text-sm text-gray-500">Pincode: {range.pincode} | Date: {range.date}</p>
                          </div>
                          <Button data-testid={`delete-price-${idx}`} size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteItem('admin/price-ranges', range.id, 'Price Range')}>
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </Button>
                        </div>
                        
                        {/* Prices */}
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          <div className="text-center p-2 bg-white rounded border">
                            <p className="text-xs text-gray-500">Start</p>
                            <p className="font-semibold text-gray-700">₹{range.startPrice || '-'}</p>
                          </div>
                          <div className="text-center p-2 bg-red-50 rounded border border-red-200">
                            <p className="text-xs text-red-500">Peak</p>
                            <p className="font-semibold text-red-600">₹{range.peakPrice || '-'}</p>
                          </div>
                          <div className="text-center p-2 bg-green-50 rounded border border-green-200">
                            <p className="text-xs text-green-500">Low</p>
                            <p className="font-semibold text-green-600">₹{range.lowPrice || '-'}</p>
                          </div>
                          <div className="text-center p-2 bg-blue-50 rounded border border-blue-200">
                            <p className="text-xs text-blue-500">Avg</p>
                            <p className="font-semibold text-blue-600">₹{range.averagePrice || '-'}</p>
                          </div>
                        </div>

                        {/* Time Ranges */}
                        {(range.peakTimeStart || range.lowTimeStart || range.avgTimeStart) && (
                          <div className="space-y-1 text-xs">
                            {range.peakTimeStart && (
                              <p className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                <span className="text-gray-600">Peak Time:</span>
                                <span className="font-medium">{range.peakTimeStart} - {range.peakTimeEnd}</span>
                              </p>
                            )}
                            {range.lowTimeStart && (
                              <p className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                <span className="text-gray-600">Low Time:</span>
                                <span className="font-medium">{range.lowTimeStart} - {range.lowTimeEnd}</span>
                              </p>
                            )}
                            {range.avgTimeStart && (
                              <p className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                <span className="text-gray-600">Avg Time:</span>
                                <span className="font-medium">{range.avgTimeStart} - {range.avgTimeEnd}</span>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )) : (
                      <p className="text-gray-500 text-sm">No price ranges configured yet</p>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* AI Pricing & Menu Intelligence */}
          {activeSection === 'ai-pricing' && (
            <Card className="p-6 bg-white" data-testid="ai-pricing-section">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5 text-[#0a7d4f]" />
                AI Pricing & Menu Intelligence
              </h3>

              {/* Filters */}
              <div className="flex flex-wrap items-end gap-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg mb-6">
                <div>
                  <Label htmlFor="aiCity" className="text-sm font-medium">City</Label>
                  <Select value={aiCity} onValueChange={setAiCity}>
                    <SelectTrigger className="w-40 bg-white">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIES.map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="aiDate" className="text-sm font-medium">Date</Label>
                  <Input 
                    id="aiDate"
                    type="date" 
                    value={aiDate}
                    onChange={(e) => setAiDate(e.target.value)}
                    className="w-40 bg-white"
                  />
                </div>
                <Button 
                  onClick={runAIAnalysis}
                  disabled={aiLoading}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                >
                  {aiLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Run AI Analysis</>
                  )}
                </Button>
              </div>

              {/* Category Price Index Summary */}
              <div className="mb-8">
                <h4 className="text-md font-semibold mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-purple-600" />
                  Category Price Index Summary
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-purple-50">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Avg Price (₹)</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Index Value</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Total Dishes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categorySummary.length > 0 ? categorySummary.map((cat, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">{cat.category}</td>
                          <td className="px-4 py-3 text-sm">₹{cat.avgPrice}</td>
                          <td className="px-4 py-3">
                            <Badge className={
                              cat.indexValue > 100 
                                ? 'bg-green-100 text-green-700' 
                                : cat.indexValue < 100 
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 text-gray-700'
                            }>
                              {cat.indexValue?.toFixed(1)}
                              {cat.indexValue > 100 ? ' ↑' : cat.indexValue < 100 ? ' ↓' : ''}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm">{cat.totalDishes}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                            Click "Run AI Analysis" to generate category summary
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Dish Price Fluctuation Summary */}
              <div>
                <h4 className="text-md font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  Dish Price Fluctuation Summary
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-blue-50">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Dish</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Avg Price</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Start</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">High</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Low</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dishSummary.length > 0 ? dishSummary.map((dish, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">{dish.dish}</td>
                          <td className="px-4 py-3 text-sm">{dish.category}</td>
                          <td className="px-4 py-3 text-sm font-semibold">₹{dish.avgPrice}</td>
                          <td className="px-4 py-3 text-sm">₹{dish.start}</td>
                          <td className="px-4 py-3 text-sm text-green-600 font-medium">₹{dish.high}</td>
                          <td className="px-4 py-3 text-sm text-red-600 font-medium">₹{dish.low}</td>
                          <td className="px-4 py-3">
                            <Badge className={
                              dish.status === 'rising' 
                                ? 'bg-green-100 text-green-700' 
                                : dish.status === 'falling' 
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                            }>
                              {dish.status === 'rising' && <TrendingUp className="w-3 h-3 mr-1 inline" />}
                              {dish.status === 'falling' && <TrendingUp className="w-3 h-3 mr-1 inline rotate-180" />}
                              {dish.status?.charAt(0).toUpperCase() + dish.status?.slice(1)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-purple-300 text-purple-600 hover:bg-purple-50"
                              onClick={() => handleSetPrice(dish)}
                            >
                              <Zap className="w-3 h-3 mr-1" /> Set Price
                            </Button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                            Click "Run AI Analysis" to generate dish price fluctuations
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          )}

          {/* Complaints & Feedback Section */}
          {activeSection === 'complaints' && (
            <Card className="p-6 bg-white" data-testid="complaints-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#0a7d4f]" />
                  Complaints & Feedback
                </h3>
                <div className="flex items-center gap-4">
                  {/* Stats badges */}
                  <div className="flex gap-2 text-sm">
                    <Badge className="bg-yellow-100 text-yellow-700">
                      Pending: {complaintsStats.by_status?.pending || 0}
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-700">
                      In Progress: {complaintsStats.by_status?.in_progress || 0}
                    </Badge>
                    <Badge className="bg-green-100 text-green-700">
                      Resolved: {complaintsStats.by_status?.resolved || 0}
                    </Badge>
                  </div>
                  {/* Filter */}
                  <Select value={complaintsFilter} onValueChange={setComplaintsFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Contact</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Subject</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complaints.length > 0 ? complaints
                      .filter(c => complaintsFilter === 'all' || c.status === complaintsFilter)
                      .map((complaint, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Badge className={
                            complaint.type === 'complaint' ? 'bg-red-100 text-red-700' :
                            complaint.type === 'feedback' ? 'bg-blue-100 text-blue-700' :
                            complaint.type === 'suggestion' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {complaint.type?.toUpperCase()}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">{complaint.name}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col">
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {complaint.phone}
                            </span>
                            {complaint.email && (
                              <span className="flex items-center gap-1 text-gray-500">
                                <Mail className="w-3 h-3" /> {complaint.email}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm max-w-[200px] truncate" title={complaint.subject}>
                          {complaint.subject}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(complaint.status)}</td>
                        <td className="px-4 py-3 text-sm">{complaint.created_at?.split('T')[0]}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedComplaint(complaint);
                                setReplyMessage(complaint.admin_reply || '');
                                setReplyStatus(complaint.status === 'pending' ? 'in_progress' : 'resolved');
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                            <Button data-testid={`delete-complaint-${idx}`} size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteItem('admin/complaints', complaint.id, 'Complaint')}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No complaints or feedback found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Complaint Detail & Reply Modal */}
              {selectedComplaint && (
                <Dialog open={!!selectedComplaint} onOpenChange={() => setSelectedComplaint(null)}>
                  <DialogContent className="sm:max-w-xl">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-[#0a7d4f]" />
                        {selectedComplaint.type?.toUpperCase()} Details
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      {/* User Info */}
                      <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500">Name</p>
                          <p className="font-medium">{selectedComplaint.name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="font-medium flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {selectedComplaint.phone}
                          </p>
                        </div>
                        {selectedComplaint.email && (
                          <div className="col-span-2">
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="font-medium flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {selectedComplaint.email}
                            </p>
                          </div>
                        )}
                        {selectedComplaint.order_id && (
                          <div>
                            <p className="text-xs text-gray-500">Order ID</p>
                            <p className="font-medium">{selectedComplaint.order_id}</p>
                          </div>
                        )}
                        {selectedComplaint.restaurant_name && (
                          <div>
                            <p className="text-xs text-gray-500">Restaurant</p>
                            <p className="font-medium">{selectedComplaint.restaurant_name}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Complaint Content */}
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-500">Subject</p>
                          <p className="font-medium">{selectedComplaint.subject}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Message</p>
                          <p className="p-3 bg-gray-50 rounded-lg text-sm">{selectedComplaint.message}</p>
                        </div>
                      </div>
                      
                      {/* Previous Reply */}
                      {selectedComplaint.admin_reply && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-xs text-gray-500 mb-1">Previous Reply ({selectedComplaint.replied_at?.split('T')[0]})</p>
                          <p className="text-sm">{selectedComplaint.admin_reply}</p>
                        </div>
                      )}
                      
                      {/* Reply Form */}
                      <div className="space-y-3 pt-4 border-t">
                        <div>
                          <Label>Reply Message</Label>
                          <Textarea 
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            placeholder="Type your reply here..."
                            rows={4}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Update Status</Label>
                          <Select value={replyStatus} onValueChange={setReplyStatus}>
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button 
                            className="flex-1 bg-[#0a7d4f] hover:bg-[#086b43]"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/admin/complaints/${selectedComplaint.id}/reply`, {
                                  complaint_id: selectedComplaint.id,
                                  reply_message: replyMessage,
                                  status: replyStatus
                                });
                                toast.success('Reply sent successfully');
                                setSelectedComplaint(null);
                                fetchAllData();
                              } catch (error) {
                                toast.error('Failed to send reply');
                              }
                            }}
                            disabled={!replyMessage.trim()}
                          >
                            <Mail className="w-4 h-4 mr-2" /> Send Reply
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => setSelectedComplaint(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </Card>
          )}

          {/* Customer Inquiries */}
          {activeSection === 'customers' && (
            <Card className="p-6 bg-white" data-testid="customers-section">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#0a7d4f]" />
                Customer Inquiries
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Phone</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">City</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Pincode</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerInquiries.length > 0 ? customerInquiries.map((inquiry, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium">{inquiry.name}</td>
                        <td className="px-4 py-3 text-sm">{inquiry.phone}</td>
                        <td className="px-4 py-3 text-sm">{inquiry.city}</td>
                        <td className="px-4 py-3 text-sm">{inquiry.pincode}</td>
                        <td className="px-4 py-3">{getStatusBadge(inquiry.status)}</td>
                        <td className="px-4 py-3 text-sm">{inquiry.created_at?.split('T')[0]}</td>
                        <td className="px-4 py-3">
                          <Button data-testid={`delete-inquiry-${idx}`} size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteItem('admin/customers', inquiry.id, 'Customer Inquiry')}>
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No customer inquiries found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Withdrawal Requests Section */}
          {activeSection === 'withdrawals' && (
            <Card className="p-6 bg-white" data-testid="withdrawals-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-[#0a7d4f]" />
                  Withdrawal Requests
                </h3>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-700">Pending</p>
                  <p className="text-2xl font-bold text-yellow-800">{withdrawalStats.pending || 0}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">Approved</p>
                  <p className="text-2xl font-bold text-blue-800">{withdrawalStats.approved || 0}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700">Completed</p>
                  <p className="text-2xl font-bold text-green-800">{withdrawalStats.completed || 0}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">Rejected</p>
                  <p className="text-2xl font-bold text-red-800">{withdrawalStats.rejected || 0}</p>
                </div>
              </div>

              {/* Filter */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {['all', 'pending', 'approved', 'processing', 'completed', 'rejected'].map((f) => (
                  <Button
                    key={f}
                    size="sm"
                    variant={withdrawalFilter === f ? 'default' : 'outline'}
                    className={withdrawalFilter === f ? 'bg-[#0a7d4f] hover:bg-[#086b43]' : ''}
                    onClick={() => setWithdrawalFilter(f)}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </Button>
                ))}
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Phone</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.filter(w => withdrawalFilter === 'all' || w.status === withdrawalFilter).length > 0 ?
                      withdrawals.filter(w => withdrawalFilter === 'all' || w.status === withdrawalFilter).map((w, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium">{w.requester_name}</td>
                        <td className="px-4 py-3">
                          <Badge className={w.requester_type === 'vendor' ? 'bg-purple-100 text-purple-700' : 'bg-indigo-100 text-indigo-700'}>
                            {w.requester_type === 'vendor' ? 'Vendor' : 'Delivery'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm">{w.phone}</td>
                        <td className="px-4 py-3 text-sm font-bold text-green-700">Rs.{w.amount}</td>
                        <td className="px-4 py-3">{getStatusBadge(w.status)}</td>
                        <td className="px-4 py-3 text-sm">{w.created_at?.split('T')[0]}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 flex-wrap">
                            {w.status === 'pending' && (
                              <>
                                <Button
                                  data-testid={`approve-withdrawal-${idx}`}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => handleWithdrawalAction(w.id, 'approve')}
                                >
                                  Accept
                                </Button>
                                <Button
                                  data-testid={`reject-withdrawal-${idx}`}
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                  onClick={() => {
                                    const reason = window.prompt('Reason for rejection (optional):');
                                    handleWithdrawalAction(w.id, 'reject', reason || '');
                                  }}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                            {w.status === 'approved' && (
                              <Button
                                data-testid={`payment-done-${idx}`}
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => {
                                  setPaymentModal(w);
                                  setPaymentForm({ transaction_id: '', payment_method: 'Bank Transfer', remarks: '' });
                                }}
                              >
                                Payment Done
                              </Button>
                            )}
                            {w.status === 'completed' && (
                              <Button
                                data-testid={`send-details-${idx}`}
                                size="sm"
                                className="bg-[#25D366] hover:bg-[#1da851] text-white"
                                onClick={() => sendWhatsApp(w.phone, w)}
                              >
                                Send Details
                              </Button>
                            )}
                            <Button
                              data-testid={`delete-withdrawal-${idx}`}
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => handleDeleteItem('admin/withdrawals', w.id, 'Withdrawal')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No withdrawal requests found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Payment Done Modal */}
              {paymentModal && (
                <Dialog open={!!paymentModal} onOpenChange={() => setPaymentModal(null)}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-[#0a7d4f]" />
                        Payment Details — {paymentModal.requester_name}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-700">Amount: <span className="font-bold text-lg">Rs.{paymentModal.amount}</span></p>
                        <p className="text-sm text-green-600">Phone: {paymentModal.phone}</p>
                      </div>
                      <div>
                        <Label>Transaction ID</Label>
                        <Input data-testid="payment-txn-id" placeholder="e.g. UTR123456789" value={paymentForm.transaction_id} onChange={(e) => setPaymentForm({...paymentForm, transaction_id: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label>Payment Method</Label>
                        <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                            <SelectItem value="UPI">UPI</SelectItem>
                            <SelectItem value="Cash">Cash</SelectItem>
                            <SelectItem value="Cheque">Cheque</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Remarks (optional)</Label>
                        <Input data-testid="payment-remarks" placeholder="Any notes..." value={paymentForm.remarks} onChange={(e) => setPaymentForm({...paymentForm, remarks: e.target.value})} className="mt-1" />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button data-testid="confirm-payment-done" className="flex-1 bg-[#0a7d4f] hover:bg-[#086b43]" onClick={handlePaymentDone}>
                          Confirm Payment Done
                        </Button>
                        <Button variant="outline" onClick={() => setPaymentModal(null)}>Cancel</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </Card>
          )}

          {/* Trash Section */}
          {activeSection === 'trash' && (
            <Card className="p-6 bg-white" data-testid="trash-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-red-500" />
                  Trash (Recoverable for 7 days)
                </h3>
                {trashItems.length > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={async () => {
                      if (window.confirm('⚠️ This will permanently delete ALL items in trash. This cannot be undone!')) {
                        try {
                          await axios.delete(`${API}/admin/trash`);
                          toast.success('Trash emptied permanently');
                          fetchAllData();
                        } catch (error) {
                          toast.error('Failed to empty trash');
                        }
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Empty Trash
                  </Button>
                )}
              </div>
              
              {trashItems.length > 0 ? (
                <div className="space-y-4">
                  {trashItems.map((batch, idx) => (
                    <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium text-sm">Batch: {batch.batch_id?.slice(0, 8)}...</p>
                          <p className="text-xs text-gray-500">
                            Deleted: {batch.deleted_at?.split('T')[0]} | 
                            Expires: {batch.expires_at?.split('T')[0]}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Badge className="bg-red-100 text-red-700">
                            {batch.total_items} items
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Collections in this batch */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                        {batch.collections?.map((coll, cidx) => (
                          <div key={cidx} className="text-xs bg-white rounded p-2 border">
                            <p className="font-medium capitalize">{coll.collection?.replace('_', ' ')}</p>
                            <p className="text-gray-500">{coll.count} items</p>
                          </div>
                        ))}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 mt-3 pt-3 border-t">
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={async () => {
                            if (window.confirm('Restore all data from this batch?')) {
                              try {
                                await axios.post(`${API}/admin/trash/${batch.batch_id}/restore`);
                                toast.success('Data restored successfully!');
                                fetchAllData();
                              } catch (error) {
                                toast.error('Failed to restore data');
                              }
                            }
                          }}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" /> Restore All
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={async () => {
                            try {
                              const response = await axios.get(`${API}/admin/trash/${batch.batch_id}`);
                              setTrashBatchDetails(response.data);
                              setSelectedTrashBatch(batch.batch_id);
                            } catch (error) {
                              toast.error('Failed to load batch details');
                            }
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View Details
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={async () => {
                            if (window.confirm('⚠️ Permanently delete this batch? This cannot be undone!')) {
                              try {
                                await axios.delete(`${API}/admin/trash/${batch.batch_id}`);
                                toast.success('Batch deleted permanently');
                                fetchAllData();
                              } catch (error) {
                                toast.error('Failed to delete batch');
                              }
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Trash2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Trash is empty</p>
                  <p className="text-sm">Deleted data will appear here for 7 days before permanent deletion</p>
                </div>
              )}
              
              {/* Trash Batch Detail Modal */}
              {selectedTrashBatch && trashBatchDetails && (
                <Dialog open={!!selectedTrashBatch} onOpenChange={() => setSelectedTrashBatch(null)}>
                  <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="w-5 h-5 text-red-500" />
                        Trash Batch Details
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      <div className="flex gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Deleted</p>
                          <p className="font-medium">{trashBatchDetails.deleted_at?.split('T')[0]}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Expires</p>
                          <p className="font-medium text-red-600">{trashBatchDetails.expires_at?.split('T')[0]}</p>
                        </div>
                      </div>
                      
                      {trashBatchDetails.items?.map((item, idx) => (
                        <div key={idx} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium capitalize">{item.collection?.replace('_', ' ')}</h4>
                            <div className="flex items-center gap-2">
                              <Badge>{item.count} items</Badge>
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/admin/trash/${selectedTrashBatch}/restore/${item.collection}`);
                                    toast.success(`${item.collection} restored!`);
                                    setSelectedTrashBatch(null);
                                    fetchAllData();
                                  } catch (error) {
                                    toast.error('Failed to restore');
                                  }
                                }}
                              >
                                <RotateCcw className="w-3 h-3 mr-1" /> Restore
                              </Button>
                            </div>
                          </div>
                          
                          {/* Sample data preview */}
                          {item.sample_data?.length > 0 && (
                            <div className="mt-2 text-xs bg-gray-50 rounded p-2">
                              <p className="text-gray-500 mb-1">Sample (first 3 items):</p>
                              {item.sample_data.map((sample, sidx) => (
                                <div key={sidx} className="truncate text-gray-600">
                                  {JSON.stringify(sample).slice(0, 100)}...
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </Card>
          )}
        </main>
      </div>

      {/* Partner Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {partnerType === 'restaurant' ? (
                <Store className="w-5 h-5 text-[#0a7d4f]" />
              ) : (
                <Bike className="w-5 h-5 text-[#0a7d4f]" />
              )}
              {partnerType === 'restaurant' ? 'Restaurant Partner Details' : 'Delivery Partner Details'}
            </DialogTitle>
          </DialogHeader>

          {selectedPartner && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="font-medium">{partnerType === 'restaurant' ? selectedPartner.owner_name : selectedPartner.name}</p>
                  </div>
                </div>
                {partnerType === 'restaurant' && (
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Restaurant</p>
                      <p className="font-medium">{selectedPartner.restaurant_name}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="font-medium">{selectedPartner.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium">{selectedPartner.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="font-medium">{selectedPartner.city}, {selectedPartner.pincode}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Applied On</p>
                    <p className="font-medium">{selectedPartner.created_at?.split('T')[0]}</p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium">Current Status</span>
                {getStatusBadge(selectedPartner.status)}
              </div>

              {/* Documents Section */}
              {partnerType === 'restaurant' && (
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Documents
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {renderDocVerify('Restaurant Photo', selectedPartner.restaurant_photo, 'restaurant_photo')}
                    {renderDocVerify('FSSAI Certificate', selectedPartner.fssai_certificate, 'fssai')}
                    {renderDocVerify('Menu PDF', selectedPartner.menu_pdf, 'menu')}
                  </div>
                </div>
              )}

              {partnerType === 'delivery' && selectedPartner.kyc_submitted && (
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Shield className="w-4 h-4" /> KYC Documents
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {renderDocVerify('Profile Photo', selectedPartner.profile_photo, 'profile')}
                    {renderDocVerify('Aadhar Front', selectedPartner.aadhar_front, 'aadhar_front')}
                    {renderDocVerify('Aadhar Back', selectedPartner.aadhar_back, 'aadhar_back')}
                    {renderDocVerify('PAN Card', selectedPartner.pan_card, 'pan')}
                    {renderDocVerify('Driving License', selectedPartner.driving_license, 'license')}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-500">Vehicle Type</p>
                      <p className="font-medium">{selectedPartner.vehicle_type || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Vehicle Number</p>
                      <p className="font-medium">{selectedPartner.vehicle_number || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {selectedPartner.status !== 'approved' && selectedPartner.status !== 'rejected' && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleUpdateStatus(selectedPartner.id, 'approved', partnerType)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Approve
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => handleUpdateStatus(selectedPartner.id, 'rejected', partnerType)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
